/**
 * The compact permit summary, in the editor's Permit side tab and on the design page: readiness, each step's
 * status, the top problems, the last plan set, what the next generation costs, and
 * "Open Permit workspace". The work itself happens in the workspace (/designs/<id>/permit).
 */
import { ArrowRight, CheckCircle2, Circle, ClipboardCheck, XCircle } from "lucide-react";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { Badge, cx } from "../components/ui";
import { shownVersion } from "../lib/format";
import { problemsText, sortChecks, type CheckStatus } from "../lib/permits";
import { COUNTRY_REQUIRED, STEPS, luxText, stepBadge, stepStatuses, type StepState } from "../lib/permitWorkspace";
import { inputsOf } from "../lib/permitDraft";
import { requestBody, usePermitDraft, usePlanSets, usePreview } from "./data";
import { api, unwrap, type S } from "../api/client";
import { useQuery } from "@tanstack/react-query";
import { statusTone as solarAppTone, type SubmissionStatus } from "../lib/solarapp";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";
import { useFormat } from "./format";
import "./workspace.css";

/** lib/solarapp's STATUS_LABELS, as catalog keys (same ids). */
const SOLARAPP_STATUS: Record<SubmissionStatus, MessageKey> = {
  created: "permit.summary.solarAppStatus.created",
  submitted: "permit.summary.solarAppStatus.submitted",
  approved: "permit.summary.solarAppStatus.approved",
  corrections: "permit.summary.solarAppStatus.corrections",
  cancelled: "permit.summary.solarAppStatus.cancelled",
  error: "permit.summary.solarAppStatus.error",
};

const ICON: Record<StepState, React.ReactNode> = {
  problem: <XCircle size={14} className="pw-ic fail" />,
  notstarted: <Circle size={14} className="pw-ic muted" />,
  done: <CheckCircle2 size={14} className="pw-ic ok" />,
  unchecked: <Circle size={14} className="pw-ic muted" />,
};

export function PermitSummary({ designId, version, offset, shortcut, dirty = false, onOpen }: { designId: string; version: number; offset?: number | null; shortcut?: string | null; dirty?: boolean; onOpen?: (href: string) => void }) {
  const t = useT();
  const f = useFormat();
  const draft = usePermitDraft(designId); // the workspace's saved inputs (equipment, utility, AHJ, …)
  const body = useMemo(() => requestBody(inputsOf(draft.data?.inputs as Record<string, unknown> | undefined)), [draft.data]);
  const preview = usePreview(designId, draft.isLoading ? undefined : version, body, null);
  const planSets = usePlanSets(designId);
  const checks = useMemo(() => (preview.data ? sortChecks(preview.data.electrical.checks as (S["PlanSetCheck"] & { status: CheckStatus })[]) : []), [preview.data]);
  const statuses = useMemo(() => stepStatuses(checks, preview.data?.electrical.notes ?? []), [checks, preview.data]);
  const total = statuses.review;
  const problems = checks.filter((c) => c.status === "problem").slice(0, 3);
  const last = planSets.data?.items[0];
  const solarApp = useQuery({
    queryKey: ["solarapp-submissions", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/solarapp/submissions", { params: { path: { design_id: designId } } })),
    staleTime: 60_000,
  }).data?.items[0];
  const cost = preview.data?.cost_lux ?? planSets.data?.cost_lux;
  const ready = total.ok;
  const all = checks.length || 1;
  const to = `/designs/${designId}/permit`;
  const open = (
    <Link to={to} className="btn btn-primary pw-open">
      <ClipboardCheck size={16} /> {t("permit.summary.openWorkspace")} {shortcut && <kbd>{shortcut}</kbd>}
    </Link>
  );
  return (
    <div
      className="pw-summary"
      onClickCapture={(e) => {
        // the editor checks for unsaved changes before leaving
        const a = (e.target as Element).closest("a");
        if (!onOpen || !a) return;
        e.preventDefault();
        onOpen(a.getAttribute("href") ?? to);
      }}
    >
      {preview.isLoading ? (
        <div className="pw-skeleton"><div /><div /><div /></div>
      ) : preview.error ? (
        <p className="small muted">
          {dirty ? t("permit.summary.saveToCheck") : t("permit.summary.checksFailed")}
          {!dirty && (preview.error as { problem?: { code?: string } }).problem?.code === COUNTRY_REQUIRED && <> <Link to={`${to}?step=equipment`}>{t("permit.equipmentStep.countryChoose")}</Link></>}
        </p>
      ) : (
        <>
          <div className="pw-sum-head">
            <div className="pw-ring" style={{ ["--p" as string]: `${Math.round((ready / all) * 100)}` }} aria-label={t("permit.summary.ringLabel", { ready, total: checks.length })}>
              <span>{t("permit.summary.ring", { ready, total: checks.length })}</span>
            </div>
            <div>
              <b>{total.problems ? problemsText(t, total.problems) : t("permit.summary.ready")}</b>
              <div className="pw-h-chips">
                {total.problems > 0 && <span className="pw-count fail">{problemsText(t, total.problems)}</span>}
                <span className="pw-count ok">{t("permit.summary.passed", { count: total.ok })}</span>
              </div>
            </div>
          </div>
          <ul className="pw-sum-steps">
            {STEPS.filter((s) => s.id !== "review").map((s) => (
              <li key={s.id}>
                <Link to={`${to}?step=${s.id}`} className={cx("pw-sum-step", `st-${statuses[s.id].state}`)}>
                  {ICON[statuses[s.id].state]} <span>{t(s.label)}</span> <span className="muted small">{stepBadge(t, statuses[s.id])}</span>
                </Link>
              </li>
            ))}
          </ul>
          {problems.length > 0 && (
            <ul className="pw-sum-top">
              {problems.map((c) => (
                <li key={c.id}>
                  <Link to={`${to}?check=${encodeURIComponent(c.id)}`} className={cx("pw-sum-check", "fail")}>
                    <XCircle size={13} className="pw-ic fail" />
                    <span className="ellipsis" dir="auto">{c.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <div className="pw-sum-foot small">
        {last ? (
          <span>
            <FormattedMessage id="permit.summary.last" values={{ b: (c) => <b>{c}</b>, number: last.number, version: shownVersion(last.version_number, offset), date: f.date(last.created_at) }} />{" "}
            {last.status !== "ready" && <Badge tone={last.status === "failed" ? "danger" : "info"}>{last.status === "failed" ? t("permit.summary.failed") : t("permit.summary.rendering")}</Badge>}
          </span>
        ) : (
          <span className="muted">{t("permit.summary.noPlanSet")}</span>
        )}
        {cost != null && <span className="muted">{cost > 0 ? t("permit.summary.costPaid", { cost: luxText(t, cost) }) : t("permit.summary.costFree")}</span>}
        {solarApp && (
          <span>
            <FormattedMessage
              id={solarApp.permit_number ? "permit.summary.solarAppPermit" : "permit.summary.solarApp"}
              values={{
                status: <Badge tone={solarAppTone(solarApp.status as SubmissionStatus)}>{t(SOLARAPP_STATUS[solarApp.status as SubmissionStatus])}</Badge>,
                permit: <span dir="auto">{solarApp.permit_number}</span>,
              }}
            />
          </span>
        )}
      </div>
      {open}
      <Link to={`${to}?step=review`} className="link-btn small pw-sum-review">
        {t("permit.summary.review")} <DirIcon><ArrowRight size={13} /></DirIcon>
      </Link>
    </div>
  );
}
